import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';

import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import { FieldWithAction } from '@/components/FieldWithAction';
import { useState, useCallback, useMemo } from 'react';
import {
  useRecordContext,
  Labeled,
  FunctionField,
  UpdateButton,
  Edit,
  SimpleForm,
  Toolbar,
  SaveButton,
  TextInput,
  useRefresh,
} from 'react-admin';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { CopiableField } from '@/components/CopiableIdField';
import { adminRpcClient } from '@/providers/backendClient';
import { format as formatDate } from 'date-fns';

export const FreeTrialResetButton = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = useCallback(() => {
    if (!record.freeTrial.history?.length) {
      alert('No free trial history to reset');
      return;
    }
    setDialogOpen(true);
  }, [record.freeTrial.history?.length]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    adminRpcClient('PUBLIC_ADMIN_therapist_freeTrialHistory_clear', [
      record.id,
    ])
      .then(() => {
        refresh();
        setDialogOpen(false);
      })
      .catch(err => {
        alert('Error resetting free trial history: ' + err.message);
      });
  }, [record.id, refresh]);

  if (!record.freeTrial.history?.length) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleOpenDialog}
        size="small"
        variant="text"
        startIcon={<WarningIcon />}
      >
        Reset
      </Button>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the free trial history? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const FreeTrialActions = () => {
  const record = useRecordContext();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const refresh = useRefresh();

  const trialNotesBalance = useMemo(
    () =>
      (record.freeTrialNotesCount || 0) -
      (record.freeTrialNotesHistory?.length || 0),
    [record],
  );

  const remainingNotes = useMemo(
    () => Math.max(0, trialNotesBalance),
    [trialNotesBalance],
  );

  const onOpenEditDialog = useCallback(() => {
    setEditDialogOpen(true);
  }, []);

  const onCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    refresh();
  }, [refresh]);

  const onOpenHistoryDialog = useCallback(() => {
    setHistoryDialogOpen(true);
  }, []);

  const onCloseHistoryDialog = useCallback(() => {
    setHistoryDialogOpen(false);
  }, []);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap="0.5rem"
        flexWrap="wrap"
        onClick={e => e.stopPropagation()}
      >
        <Labeled label="Trial Notes Usage">
          <FieldWithAction
            value={(record.freeTrial.usage || 0).toString()}
            maxWidth="120px"
            showActionOnHover={false}
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="Trial Notes Balance">
          <FieldWithAction
            value={(record.freeTrial.balance || 0).toString()}
            maxWidth="120px"
            showActionOnHover={false}
            action={
              <IconButton
                size="small"
                onClick={onOpenHistoryDialog}
                color="primary"
                sx={{ padding: '4px' }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            }
          />
        </Labeled>
        <Labeled label="Granted Free Trial Notes" marginLeft="1rem">
          <FieldWithAction
            value={
              !record.freeTrial.count
                ? '🟢 Unlimited'
                : record.freeTrial.count.toString()
            }
            maxWidth="200px"
            showActionOnHover={false}
            action={
              <IconButton
                onClick={onOpenEditDialog}
                size="small"
                color="primary"
                sx={{ padding: '4px' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            }
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="Popup Displayed At">
          <FieldWithAction
            value={
              record.freeTrialPopupDisplayedAt
                ? (() => {
                    try {
                      const date = new Date(record.freeTrialPopupDisplayedAt);
                      if (!isNaN(date.getTime())) {
                        return formatDate(
                          date,
                          'MM/dd/yyyy hh:mm:ss a',
                        );
                      }
                    } catch {
                      // Invalid date
                    }
                    return null;
                  })()
                : null
            }
            emptyText="Never"
            showActionOnHover={false}
          />
        </Labeled>
      </Box>
      <Dialog open={editDialogOpen} onClose={onCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Granted Free Trial Notes</DialogTitle>
        <Edit
          mutationMode="pessimistic"
          mutationOptions={{ onSuccess: onCloseEditDialog }}
          resource="therapists"
          queryOptions={{ meta: { raw: true } }}
          id={record.id}
        >
          <SimpleForm
            toolbar={
              <Toolbar>
                <DialogActions>
                  <Button
                    color="secondary"
                    onClick={onCloseEditDialog}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <SaveButton
                    resource="therapists"
                    label="Set Notes Count"
                    color="primary"
                    type="button"
                    transform={rec => ({
                      freeTrialNotesCount: parseInt(
                        rec.freeTrialNotesCount,
                      ),
                    })}
                    mutationOptions={{
                      onSuccess: () => {
                        onCloseEditDialog();
                        refresh();
                      },
                    }}
                  />
                </DialogActions>
              </Toolbar>
            }
          >
            <TextInput
              fullWidth
              autoFocus
              source="freeTrialNotesCount"
              label="Granted Free Trial Notes"
              type="number"
              required
            />
          </SimpleForm>
        </Edit>
      </Dialog>
      <Dialog open={historyDialogOpen} onClose={onCloseHistoryDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Free Trial Notes Usage History
        </DialogTitle>
        <Box sx={{ padding: '1rem' }}>
          {record.freeTrial.history?.map((note: any) => (
            <Box key={note._id} display="flex" marginBottom="1rem">
              <Labeled label="Id">
                <CopiableField
                  targetNavigationResource="therapistSessionNotes"
                  targetNavigationFilters={{
                    _id: { $oid: note._id },
                    isHiddenTestAccount: 'visible',
                  }}
                  content={note._id}
                />
              </Labeled>
              <Labeled marginLeft="1rem" label="Date">
                <GenericRelativeDateDisplay date={note.when} />
              </Labeled>
              <Labeled marginLeft="1rem" label="Entry Type">
                <Chip label={note.entryType} />
              </Labeled>
              <Labeled marginLeft="1rem" label="Media Type">
                <Chip label={note.mediaType} />
              </Labeled>
              <Labeled marginLeft="1rem" label="Word Count">
                <Typography>{note.wordCount}</Typography>
              </Labeled>
              <Labeled marginLeft="1rem" label="Trial Notes Left">
                <Typography>{note.notesLeft}</Typography>
              </Labeled>
            </Box>
          ))}
        </Box>
        <DialogActions>
          <Button onClick={onCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const FreeTrialInfo = () => {
  const record = useRecordContext();

  const trialNotesBalance = useMemo(
    () =>
      (record.freeTrialNotesCount || 0) -
      (record.freeTrialNotesHistory?.length || 0),
    [record],
  );

  return (
    <Box>
      {!!trialNotesBalance && (
        <Labeled label="Actions">
          <Box display="flex">
            <UpdateButton
              resource="therapists"
              label="Clear"
              type="button"
              color="error"
              variant="contained"
              mutationMode="pessimistic"
              confirmTitle="Are you sure?"
              confirmContent="This will make the user stay forever on a Lifelong Trial"
              icon={<ClearIcon />}
              sx={{ color: '#fff' }}
              data={{
                freeTrialNotesCount: null,
                freeTrialNotesHistory: [],
                freeTrialPopupDisplayedAt: null,
              }}
            />
          </Box>
        </Labeled>
      )}
    </Box>
  );
};
